import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import { t } from "@lingui/core/macro";
import HebcalService from "../../../services/hebcal";
import { restPeriodTitle, restPeriodRowLabel } from "../../../services/restPeriodDisplay";
import { resolveUrl } from "../utils";
import { sharedStyles, SectionHead } from "./_shared";

/**
 * Section « Horaires de Chabbat » (thème 2026).
 *
 * Affiche le PROCHAIN Chabbat pour la position géo de l'org : entrée (allumage
 * des bougies), sortie (havdalah), nom de la paracha, date grégorienne +
 * hébraïque, et — quand applicable — les mentions Chabbat Mevarhim / Roch Hodech.
 * Un CTA optionnel (label + url) peut être ajouté (ex: réserver les repas).
 *
 * Données calculées CÔTÉ FRONT via HebcalService (même classe que la page
 * Horaires) — aucune dépendance API. Tout est dérivé de `org.lat/long` +
 * `org.zmanimShitat`.
 *
 * 3 styles (prop `style`, choisie par l'admin) :
 *   - BANNER : bandeau pleine largeur (défaut)
 *   - CARD   : carte centrée compacte
 *   - TILES  : bandeau paracha + 2 grandes tuiles d'horaires
 *
 * COULEURS : 100% dérivées des tokens du thème (--primary, --secondary,
 * --surface, --ink, --line…) → s'adapte à n'importe quelle palette d'org.
 */

const styles = `
.t26-sht { max-width: var(--container); margin-inline: auto; padding: clamp(28px,4vw,56px) clamp(16px,4vw,32px); }
.t26-sht__cta {
  display: inline-flex; align-items: center; gap: 8px;
  text-decoration: none; font-size: 14px; font-weight: 700;
  padding: 11px 22px; border-radius: var(--r-pill, 999px);
  transition: transform .15s ease, box-shadow .15s ease;
}
.t26-sht__cta:hover { transform: translateY(-1px); box-shadow: var(--shadow-md); }
.t26-sht__cta i { font-size: 16px; }

/* ---- shared time chips ---- */
.t26-sht__time-ic { font-size: 22px; color: var(--secondary); line-height: 1; }
.t26-sht__time-lbl { font-size: 11px; text-transform: uppercase; letter-spacing: .1em; }
.t26-sht__time-val { font-family: var(--font-display); font-weight: 700; }
.t26-sht__time-day { font-size: 11px; opacity: .78; margin-top: 3px; }
.t26-sht__badge {
  display: inline-block; font-size: 11px; font-weight: 600;
  padding: 4px 12px; border-radius: 999px; margin-top: 2px;
}

/* ================= BANNER ================= */
.t26-sht--banner .t26-sht__inner {
  background: var(--primary); color: var(--primary-contrast);
  border-radius: 18px; padding: clamp(20px,3vw,28px);
}
.t26-sht--banner .t26-sht__top { display: flex; flex-wrap: wrap; align-items: center; gap: 24px; }
.t26-sht--banner .t26-sht__lead { flex: 1; min-width: 200px; }
.t26-sht--banner .t26-sht__eyebrow {
  font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
  color: var(--secondary); font-weight: 700; margin-bottom: 6px;
}
.t26-sht--banner .t26-sht__parasha { font-family: var(--font-display); font-size: clamp(22px,2.6vw,27px); line-height: 1.1; }
.t26-sht--banner .t26-sht__dates { font-size: 13px; opacity: .85; margin-top: 5px; }
.t26-sht--banner .t26-sht__badge { background: color-mix(in oklab, var(--secondary) 26%, transparent); color: var(--secondary); margin-top: 10px; }
.t26-sht--banner .t26-sht__times { display: flex; gap: 14px; flex-wrap: wrap; }
.t26-sht--banner .t26-sht__chip {
  background: color-mix(in oklab, var(--primary-contrast) 9%, transparent);
  border-radius: 12px; padding: 14px 20px; text-align: center; min-width: 116px;
}
.t26-sht--banner .t26-sht__time-lbl { color: var(--secondary); margin-bottom: 6px; }
.t26-sht--banner .t26-sht__time-val { font-size: 25px; }
.t26-sht--banner .t26-sht__foot {
  border-top: 1px solid color-mix(in oklab, var(--primary-contrast) 14%, transparent);
  margin-top: 18px; padding-top: 14px; text-align: center;
}
.t26-sht--banner .t26-sht__cta { background: var(--secondary); color: var(--secondary-contrast, #1a1a1a); }

/* ================= CARD ================= */
.t26-sht--card .t26-sht__inner {
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 18px; padding: clamp(24px,3vw,32px);
  max-width: 420px; margin-inline: auto; text-align: center;
  box-shadow: var(--shadow-sm);
}
.t26-sht--card .t26-sht__eyebrow {
  font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
  color: var(--secondary); font-weight: 700; margin-bottom: 10px;
}
.t26-sht--card .t26-sht__parasha { font-family: var(--font-display); font-size: 24px; color: var(--ink); margin-bottom: 3px; }
.t26-sht--card .t26-sht__dates { font-size: 13px; color: var(--ink-3); }
.t26-sht--card .t26-sht__hdate { font-size: 12px; color: var(--ink-3); opacity: .8; }
.t26-sht--card .t26-sht__times { display: flex; flex-wrap: wrap; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); margin-top: 18px; }
.t26-sht--card .t26-sht__chip { flex: 1 1 33%; padding: 16px 8px; }
.t26-sht--card .t26-sht__chip + .t26-sht__chip { border-inline-start: 1px solid var(--line); }
.t26-sht--card .t26-sht__time-lbl { color: var(--ink-3); margin: 4px 0 0; }
.t26-sht--card .t26-sht__time-val { font-size: 22px; color: var(--ink); }
.t26-sht--card .t26-sht__badge { background: color-mix(in oklab, var(--secondary) 16%, var(--surface)); color: color-mix(in oklab, var(--secondary) 80%, var(--ink)); margin-top: 16px; }
.t26-sht--card .t26-sht__cta { background: var(--primary); color: var(--primary-contrast); margin-top: 18px; }

/* ================= TILES ================= */
.t26-sht--tiles .t26-sht__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; }
.t26-sht--tiles .t26-sht__head {
  grid-column: 1 / -1; background: var(--primary); color: var(--primary-contrast);
  border-radius: 14px; padding: 16px 22px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
}
.t26-sht--tiles .t26-sht__head-ic { font-size: 24px; color: var(--secondary); }
.t26-sht--tiles .t26-sht__eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: .15em; color: var(--secondary); }
.t26-sht--tiles .t26-sht__parasha { font-family: var(--font-display); font-size: 21px; }
.t26-sht--tiles .t26-sht__meta { margin-inline-start: auto; text-align: end; font-size: 12px; opacity: .9; }
.t26-sht--tiles .t26-sht__meta .t26-sht__badge { background: transparent; color: var(--secondary); padding: 0; margin: 2px 0 0; }
.t26-sht--tiles .t26-sht__tile { background: var(--surface); border: 1px solid var(--line); border-radius: 14px; padding: 20px; text-align: center; box-shadow: var(--shadow-sm); }
.t26-sht--tiles .t26-sht__time-lbl { color: var(--secondary); margin-bottom: 6px; }
.t26-sht--tiles .t26-sht__time-val { font-size: 30px; color: var(--ink); }
.t26-sht--tiles .t26-sht__day { font-size: 12px; color: var(--ink-3); margin-top: 2px; }
.t26-sht--tiles .t26-sht__foot { grid-column: 1 / -1; text-align: center; }
.t26-sht--tiles .t26-sht__cta { background: var(--secondary); color: var(--secondary-contrast, #1a1a1a); }

@media (max-width: 640px) {
  .t26-sht--banner .t26-sht__times { width: 100%; }
  .t26-sht--banner .t26-sht__chip { flex: 1; }
}
`;

/**
 * Formate une heure (Date astronomique = instant UTC) en HH:MM DANS LE FUSEAU
 * DE L'ORG (`tz` = nom IANA). Sans `tz`, on retombe sur le fuseau du
 * navigateur — d'où le décalage historique pour les org à l'étranger.
 */
function fmtTime(date, locale, tz) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(locale || "fr", {
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz || undefined,
    }).format(date);
}

/** Jour de la semaine (Date) localisé, ex "vendredi", dans le fuseau de l'org. */
function fmtWeekday(date, locale, tz) {
    if (!date || isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(locale || "fr", {
        weekday: "short", day: "numeric", month: "short", timeZone: tz || undefined,
    }).format(date);
}

/** Plage de dates "ven. 3 — sam. 4 juillet 2026" (compacte, localisée), dans le fuseau de l'org. */
function fmtRange(entree, sortie, locale, tz) {
    if (!entree) return "";
    const dOpts = { day: "numeric", month: "long", year: "numeric", timeZone: tz || undefined };
    const sameDay = (a, b) =>
        new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz || undefined }).format(a) ===
        new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz || undefined }).format(b);
    if (sortie && !sameDay(entree, sortie)) {
        const a = new Intl.DateTimeFormat(locale || "fr", { day: "numeric", month: "short", timeZone: tz || undefined }).format(entree);
        const b = new Intl.DateTimeFormat(locale || "fr", dOpts).format(sortie);
        return `${a} — ${b}`;
    }
    return new Intl.DateTimeFormat(locale || "fr", dOpts).format(entree);
}

/**
 * Calcule les infos du prochain Chabbat via HebcalService (côté front).
 * Renvoie null si la position de l'org est inconnue (pas de calcul possible).
 */
function computeShabbat(org, locale) {
    if (!org || org.lat == null || org.long == null) return null;
    try {
        const hebcal = new HebcalService(org, new Date(), org.lat, org.long);
        hebcal.generateHebCal();

        const now = new Date();
        const period = hebcal.getNextRestPeriod(now);
        if (!period) return null;

        // Rattacher les badges à l'entrée de la période affichée, même si
        // celle-ci a déjà commencé (samedi ou second jour de fête).
        let shabItem = null;
        if (Array.isArray(hebcal.HEBCAL)) {
            const parts = new Intl.DateTimeFormat("en-GB", {
                year: "numeric", month: "2-digit", day: "2-digit", timeZone: period.timeZone,
            }).formatToParts(period.start);
            const field = type => Number(parts.find(part => part.type === type)?.value);
            shabItem = hebcal.HEBCAL.find(it => it.entreeShabbat &&
                it.date.getFullYear() === field("year") &&
                it.date.getMonth() + 1 === field("month") &&
                it.date.getDate() === field("day")) || null;
        }

        // Date hébraïque (jour du samedi) si dispo.
        let hdateStr = null;
        try {
            if (!period.occasion.holidays.length && shabItem && shabItem.hdate && typeof shabItem.hdate.render === "function") {
                hdateStr = shabItem.hdate.render(locale === "he" ? "he" : "en");
            }
        } catch (e) { hdateStr = null; }

        const ordinaryShabbat = period.occasion.holidays.length === 0;
        const mevarhim = ordinaryShabbat && shabItem?.mevarhim ? (shabItem.mevarhimMonth || true) : null;
        const roshHodesh = ordinaryShabbat && shabItem?.roshHodesh ? (shabItem.roshHodeshMonth || true) : null;

        return {
            period,
            hdateStr,
            mevarhim,
            roshHodesh,
            tz: period.timeZone,
        };
    } catch (e) {
        return null;
    }
}

/** Construit la chaîne de badge "Chabbat Mevarhim · Roch Hodech <mois>". */
function buildBadge(info) {
    const parts = [];
    if (info.mevarhim) parts.push(t`Chabbat Mevarhim`);
    if (info.roshHodesh) {
        const m = typeof info.roshHodesh === "string" ? info.roshHodesh : "";
        parts.push(m ? t`Roch Hodech` + " " + m : t`Roch Hodech`);
    }
    return parts.length ? parts.join(" · ") : null;
}

export default function ShabbatTimes({ context, style = "BANNER", eyebrow, title, subtitle, ctaLabel, ctaUrl }) {
    const { org, langValue } = context;
    const locale = langValue || "fr";

    const info = computeShabbat(org, locale);
    if (!info) return null;

    const tz = info.tz;
    const schedule = info.period.schedule.map(item => ({
        ...item,
        label: restPeriodRowLabel(item, info.period),
        time: fmtTime(item.at, locale, tz),
        day: fmtWeekday(item.at, locale, tz),
    }));
    const range = fmtRange(info.period.start, info.period.end, locale, tz);
    const badge = buildBadge(info);
    const cta = ctaLabel && ctaUrl ? { label: ctaLabel, url: resolveUrl(org, ctaUrl) } : null;
    const parashaLabel = restPeriodTitle(info.period, locale);
    const defaultEyebrow = eyebrow || t`Horaires`;
    const city = org.ville || "";

    const variant =
        style === "CARD" ? "t26-sht--card" :
        style === "TILES" ? "t26-sht--tiles" :
        "t26-sht--banner";

    const CtaLink = cta ? (
        <Link href={cta.url} className="t26-sht__cta">
            <Icon name="meal" /> {cta.label}
        </Link>
    ) : null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section className={`t26-sht ${variant}`}>
                {(title || subtitle) && <SectionHead title={title} subtitle={subtitle} />}

                {style === "BANNER" && (
                    <div className="t26-sht__inner">
                        <div className="t26-sht__top">
                            <div className="t26-sht__lead">
                                <div className="t26-sht__eyebrow">{defaultEyebrow}{city ? ` · ${city}` : ""}</div>
                                <div className="t26-sht__parasha">{parashaLabel}</div>
                                <div className="t26-sht__dates">{range}{info.hdateStr ? ` · ${info.hdateStr}` : ""}</div>
                                {badge && <span className="t26-sht__badge">{badge}</span>}
                            </div>
                            <div className="t26-sht__times">
                                {schedule.map((item, index) => (
                                    <div className="t26-sht__chip" key={index}>
                                        <div className="t26-sht__time-lbl"><Icon name={item.kind === "exit" ? "stars" : "candles"} /> {item.label}</div>
                                        <div className="t26-sht__time-val">{item.time}</div>
                                        <div className="t26-sht__time-day">{item.day}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {CtaLink && <div className="t26-sht__foot">{CtaLink}</div>}
                    </div>
                )}

                {style === "CARD" && (
                    <div className="t26-sht__inner">
                        <div className="t26-sht__eyebrow">{defaultEyebrow}</div>
                        <div className="t26-sht__parasha">{parashaLabel}</div>
                        <div className="t26-sht__dates">{city ? `${city} · ` : ""}{range}</div>
                        {info.hdateStr && <div className="t26-sht__hdate">{info.hdateStr}</div>}
                        <div className="t26-sht__times">
                            {schedule.map((item, index) => (
                                <div className="t26-sht__chip" key={index}>
                                    <div className="t26-sht__time-ic"><Icon name={item.kind === "exit" ? "stars" : "candles"} /></div>
                                    <div className="t26-sht__time-lbl">{item.label}</div>
                                    <div className="t26-sht__time-val">{item.time}</div>
                                    <div className="t26-sht__time-day">{item.day}</div>
                                </div>
                            ))}
                        </div>
                        {badge && <span className="t26-sht__badge">{badge}</span>}
                        {CtaLink}
                    </div>
                )}

                {style === "TILES" && (
                    <div className="t26-sht__grid">
                        <div className="t26-sht__head">
                            <span className="t26-sht__head-ic"><Icon name="book" /></span>
                            <div>
                                <div className="t26-sht__eyebrow">{defaultEyebrow}</div>
                                <div className="t26-sht__parasha">{parashaLabel}</div>
                            </div>
                            <div className="t26-sht__meta">
                                <div>{range}{info.hdateStr ? ` · ${info.hdateStr}` : ""}</div>
                                {badge && <span className="t26-sht__badge">{badge}</span>}
                            </div>
                        </div>
                        {schedule.map((item, index) => (
                            <div className="t26-sht__tile" key={index}>
                                <div className="t26-sht__time-lbl"><Icon name={item.kind === "exit" ? "stars" : "candles"} /> {item.label}</div>
                                <div className="t26-sht__time-val">{item.time}</div>
                                <div className="t26-sht__day">{item.day}</div>
                            </div>
                        ))}
                        {CtaLink && <div className="t26-sht__foot">{CtaLink}</div>}
                    </div>
                )}
            </section>
        </>
    );
}
